'use client';

import React, { useState } from 'react';
import MpesaCheckout from '@/components/MpesaCheckout';

type Tier = 'student' | 'undergraduate' | 'premium' | 'seniors';

interface Plan {
  tier: Tier;
  label: string;
  price: number;
  description: string;
  features: string[];
  gradient: string;
  badge?: string;
}

const PLANS: Plan[] = [
  {
    tier: 'student',
    label: 'Student',
    price: 1800,
    description: 'For students starting their growth journey',
    features: [
      'Voice chat with Rafeeq AI',
      'Daily motivational videos',
      'Habit tracking & streaks',
      'Prayer times & Qibla',
      '5-language support',
    ],
    gradient: 'from-emerald-500 to-teal-600',
  },
  {
    tier: 'undergraduate',
    label: 'Undergraduate',
    price: 2400,
    description: 'For undergraduates building their future',
    features: [
      'Everything in Student',
      'Academic Research Mode',
      'Extended conversation history',
      'Priority AI responses',
      'Weekly growth analytics',
    ],
    gradient: 'from-blue-500 to-indigo-600',
    badge: 'Popular',
  },
  {
    tier: 'premium',
    label: 'Premium',
    price: 3600,
    description: 'For professionals & entrepreneurs',
    features: [
      'Everything in Undergraduate',
      'Career Launchpad access',
      'Advanced voice integration',
      'Unlimited AI coaching',
      'Business goal tracking',
    ],
    gradient: 'from-amber-500 to-orange-600',
  },
  {
    tier: 'seniors',
    label: 'Seniors',
    price: 3200,
    description: 'Exclusive ibada guidance for elders',
    features: [
      'Personalised ibada guidance',
      'Private Scholar Q&A (ask any Sheikh)',
      'Record or type your question',
      'Answers delivered privately to you',
      'Curated Deen content for seniors',
      'Prayer & Dhikr reminders',
    ],
    gradient: 'from-purple-600 to-violet-700',
    badge: 'Seniors Only',
  },
];

export default function MpesaPricingPage() {
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [successReceipt, setSuccessReceipt] = useState('');

  const handleSuccess = (receiptNumber: string) => {
    setSuccessReceipt(receiptNumber);
    setPaymentSuccess(true);
    setSelectedPlan(null);
  };

  if (paymentSuccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-12 h-12 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-800 text-foreground mb-2">Welcome to Rafeeq Premium! 🎉</h1>
            <p className="text-muted-foreground">Your subscription is now active. Start your growth journey.</p>
          </div>
          {successReceipt && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <p className="text-xs text-green-700 font-600 mb-1">M-Pesa Receipt</p>
              <p className="text-green-800 font-700 text-xl tracking-wider">{successReceipt}</p>
            </div>
          )}
          <a
            href="/"
            className="block w-full py-3 px-6 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-600 rounded-xl hover:opacity-90 transition-opacity text-center"
          >
            Go to Dashboard →
          </a>
        </div>
      </div>
    );
  }

  if (selectedPlan) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="max-w-md w-full">
          <button
            onClick={() => setSelectedPlan(null)}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm mb-6 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to plans
          </button>
          <MpesaCheckout
            tier={selectedPlan.tier}
            amount={selectedPlan.price}
            onSuccess={handleSuccess}
            onCancel={() => setSelectedPlan(null)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-1.5 rounded-full text-sm font-600 mb-4">
            <span>📱</span>
            <span>Pay with M-Pesa · Kenya</span>
          </div>
          <h1 className="text-3xl font-800 text-foreground mb-3">Choose Your Rafeeq Plan</h1>
          <p className="text-muted-foreground max-w-md mx-auto">
            Unlock AI-powered coaching, voice chat, and daily growth tools. Pay securely with M-Pesa — no card needed.
          </p>
        </div>

        {/* Plans grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          {PLANS.map((plan) => (
            <div
              key={plan.tier}
              className={`relative bg-card border rounded-2xl overflow-hidden transition-all hover:shadow-lg ${
                plan.badge ? 'border-blue-300 shadow-blue-100 shadow-md' : 'border-border'
              }`}
            >
              {plan.badge && (
                <div className="absolute top-0 right-0 bg-blue-500 text-white text-xs font-700 px-3 py-1 rounded-bl-xl">
                  {plan.badge}
                </div>
              )}

              <div className={`bg-gradient-to-r ${plan.gradient} p-6 text-white`}>
                <h3 className="text-lg font-700 mb-1">{plan.label}</h3>
                <p className="text-white/70 text-xs mb-4">{plan.description}</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-800">KES {plan.price.toLocaleString()}</span>
                  <span className="text-white/70 text-sm">/month</span>
                </div>
              </div>

              <div className="p-6">
                <ul className="space-y-2.5 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <svg className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-sm text-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => setSelectedPlan(plan)}
                  className={`w-full py-3 px-4 bg-gradient-to-r ${plan.gradient} text-white font-600 text-sm rounded-xl hover:opacity-90 transition-opacity flex items-center justify-center gap-2`}
                >
                  <span>📲</span>
                  Pay with M-Pesa
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Trust indicators */}
        <div className="bg-card border border-border rounded-2xl p-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
            {[
              { icon: '🔒', title: 'Secure Payments', desc: 'Powered by Safaricom Daraja API' },
              { icon: '⚡', title: 'Instant Activation', desc: 'Subscription active within seconds' },
              { icon: '📞', title: 'No Card Needed', desc: 'Pay directly from your M-Pesa wallet' },
            ].map((item, i) => (
              <div key={i} className="flex flex-col items-center gap-2">
                <span className="text-2xl">{item.icon}</span>
                <p className="text-sm font-600 text-foreground">{item.title}</p>
                <p className="text-xs text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Prices shown in Kenyan Shillings (KES). Subscriptions renew monthly. Cancel anytime.
        </p>
      </div>
    </div>
  );
}
